import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { BellOff, Check, Search, Undo2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { NotificationCard } from '@/components/ui/notification-card';
import { useNotificationStore, type Notification } from '@/stores/notification';
import { IoMdNotifications } from 'react-icons/io';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './drawer';
import { markNotificationAsRead } from '@/services/notificationService';

interface NotificationDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const formatNotificationDate = (date: Date | string): string => {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} - ${hours}:${minutes}`;
};

const groupNotificationsByDate = (notifications: Notification[]) => {
  const groups: Record<string, Notification[]> = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  notifications.forEach((notification) => {
    const notifDate = new Date(notification.createdAt);
    notifDate.setHours(0, 0, 0, 0);

    let key: string;
    if (notifDate.getTime() === today.getTime()) {
      key = 'Hoje';
    } else if (notifDate.getTime() === yesterday.getTime()) {
      key = 'Ontem';
    } else {
      const day = notifDate.getDate().toString().padStart(2, '0');
      const month = (notifDate.getMonth() + 1).toString().padStart(2, '0');
      key = `${day}/${month}`;
    }

    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(notification);
  });

  return groups;
};

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const {
    notifications,
    isDrawerOpen,
    setDrawerOpen,
    markAsRead,
    markAsUnread,
    markAllAsRead,
  } = useNotificationStore();

  const isOpen = open !== undefined ? open : isDrawerOpen;
  const handleOpenChange = onOpenChange || setDrawerOpen;

  const [activeTab, setActiveTab] = React.useState<'unread' | 'read'>(
    'unread',
  );
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const unreadCount = useMemo(
    () => notifications.reduce((count, item) => count + (item.isRead ? 0 : 1), 0),
    [notifications],
  );

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((n) => !n.isRead);
    }
    return notifications.filter((n) => n.isRead);
  }, [notifications, activeTab]);

  const searchedNotifications = useMemo(() => {
    if (!normalizedQuery) return filteredNotifications;

    return filteredNotifications.filter((notification) => {
      const searchableContent = [
        notification.title,
        notification.subtitle,
        notification.code,
        notification.department,
        notification.professionalName,
        notification.date,
        notification.timeRange,
        formatNotificationDate(notification.createdAt),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableContent.includes(normalizedQuery);
    });
  }, [filteredNotifications, normalizedQuery]);

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByDate(searchedNotifications);
  }, [searchedNotifications]);

  const isScaleNotification = (notification: Notification) =>
    notification.category === 'scale' || notification.category === 'schedule';

  const syncMarkAsRead = async (notification: Notification) => {
    if (notification.isRead) return;

    markAsRead(notification.id);

    if (notification.backendId) {
      try {
        await markNotificationAsRead(notification.backendId);
      } catch (error) {
        console.error(
          '[Notification] Failed to sync mark-as-read with backend:',
          error,
        );
      }
    }
  };

  const handleMarkAsRead = async (notification: Notification) => {
    await syncMarkAsRead(notification);
  };

  const handleMarkAsUnread = (notification: Notification) => {
    markAsUnread(notification.id);
  };

  const handleToggleSearch = () => {
    setIsSearchOpen((current) => {
      if (current) {
        setSearchQuery('');
      }
      return !current;
    });
  };

  return (
    <Drawer open={isOpen} onOpenChange={handleOpenChange}>
      <DrawerContent className="flex flex-col min-h-[70vh] max-w-[500px]">
        <DrawerHeader className="px-6 py-4 shadow-lg bg-background">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 flex items-center justify-center">
                  <IoMdNotifications className="w-7 h-7 text-primary" />
                </div>
              </div>
              <div>
                <DrawerTitle className="text-lg font-semibold">
                  {t('notifications.title', 'Notificações')}
                </DrawerTitle>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
              disabled={unreadCount === 0}
            >
              {t('notifications.mark_all_read', 'Marcar todas como lida')}
            </Button>
          </div>
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as 'unread' | 'read')}
            className="mt-3"
          >
            <TabsList className="grid h-8 w-full grid-cols-2 rounded-lg bg-muted/50 p-0.5">
              <TabsTrigger
                value="unread"
                className="h-7 rounded-md text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                {t('notifications.unread_tab', 'Não lidas')}
              </TabsTrigger>
              <TabsTrigger
                value="read"
                className="h-7 rounded-md text-xs text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
              >
                {t('notifications.read_tab', 'Lidas')}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={handleToggleSearch}
              className="inline-flex w-fit items-center gap-1.5 rounded-md px-0 py-0 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <Search className="h-3.5 w-3.5" />
              {t('notifications.filter', 'Filtrar')}
            </button>
          </div>
          <AnimatePresence initial={false}>
            {isSearchOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0, y: -4 }}
                animate={{ height: 'auto', opacity: 1, y: 0 }}
                exit={{ height: 0, opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="overflow-hidden"
              >
                <div className="relative mt-2">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    autoFocus
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t(
                      'notifications.search_placeholder',
                      'Filtrar notificações',
                    )}
                    className="h-9 rounded-lg border-border bg-background pl-9 text-sm shadow-sm"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DrawerHeader>

        <ScrollArea className="flex-1">
          <div className="px-4 py-4">
            <AnimatePresence mode="popLayout">
              {searchedNotifications.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                    <BellOff className="w-8 h-8 text-muted-foreground/50" />
                  </div>
                  <h3 className="font-medium text-muted-foreground mb-1">
                    {normalizedQuery
                      ? t(
                          'notifications.no_search_results',
                          'Nenhuma notificação encontrada para esse filtro',
                        )
                      : activeTab === 'unread'
                      ? t(
                          'notifications.no_unread_notifications',
                          'Nenhuma notificação não lida',
                        )
                      : t(
                          'notifications.no_read_notifications',
                          'Nenhuma notificação lida',
                        )}
                  </h3>
                  <p className="text-xs text-muted-foreground/70">
                    {t(
                      'notifications.empty_description',
                      'Quando você receber notificações, elas aparecerão aqui.',
                    )}
                  </p>
                </motion.div>
              ) : (
                Object.entries(groupedNotifications).map(([date, notifs]) => (
                  <div key={date} className="mb-6">
                    <div className="space-y-3">
                      {notifs.map((notification) => (
                        <NotificationCard
                          key={notification.id}
                          type={notification.type}
                          title={notification.title}
                          subtitle={notification.subtitle}
                          code={notification.code}
                          department={notification.department}
                          professionalName={notification.professionalName}
                          date={notification.date}
                          timeRange={notification.timeRange}
                          createdAt={formatNotificationDate(
                            notification.createdAt,
                          )}
                          isRead={notification.isRead}
                          showDismiss={false}
                          highlightQuery={searchQuery}
                          actionLabel={notification.actionLabel}
                          onAction={
                            notification.actionUrl &&
                            !isScaleNotification(notification)
                              ? () => {
                                  window.location.href =
                                    notification.actionUrl!;
                                  handleOpenChange(false);
                                }
                              : undefined
                          }
                        >
                          {!notification.isRead ? (
                            <div className="mt-2 flex justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleMarkAsRead(notification);
                                }}
                                className="h-6 px-1.5 text-[11px] font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
                              >
                                <Check className="h-3.5 w-3.5" />
                                {t(
                                  'notifications.mark_as_read',
                                  'Marcar como lida',
                                )}
                              </Button>
                            </div>
                          ) : (
                            <div className="mt-2 flex justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleMarkAsUnread(notification);
                                }}
                                className="h-6 px-1.5 text-[11px] font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
                              >
                                <Undo2 className="h-3.5 w-3.5" />
                                {t(
                                  'notifications.mark_as_unread',
                                  'Marcar como não lida',
                                )}
                              </Button>
                            </div>
                          )}
                        </NotificationCard>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
};

export default NotificationDrawer;
